import {useEffect,useState} from 'react';
import {api,ApiError,supabase} from './api';
import {queryCache} from './queryCache';

async function mediaBytes(mediaId:string,body?:File):Promise<Blob>{
 if(!supabase)throw new ApiError('unavailable','Der er ingen forbindelse til billedlageret.');
 const epoch=queryCache.sessionEpoch;
 const {data:{session}}=await supabase.auth.getSession();if(!session)throw new ApiError('forbidden','Log ind igen for at fortsætte.');
 assertEpoch(epoch);
 // Large photos on mobile connections need more time than image previews.
 const timeoutMs=body?120_000:30_000;
 const controller=new AbortController(),timer=setTimeout(()=>controller.abort(),timeoutMs);
 try{
  const response=await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cap4publish-api/media/${encodeURIComponent(mediaId)}/bytes`,{
   method:body?'PUT':'GET',cache:'no-store',signal:controller.signal,headers:{Authorization:`Bearer ${session.access_token}`,apikey:import.meta.env.VITE_SUPABASE_ANON_KEY,...(body?{'Content-Type':body.type}:{})},body,
  });
  assertEpoch(epoch);
  if(!response.ok){const detail=await response.json().catch(()=>null);assertEpoch(epoch);if(response.status===401||response.status===403)queryCache.denyAccess();throw new ApiError(detail?.code??'unavailable',detail?.message??'Billedet kunne ikke hentes. Prøv igen.')}
  const blob=await response.blob();assertEpoch(epoch);return blob;
 }catch(error){if(controller.signal.aborted)throw new ApiError('unavailable','Billedoverførslen tog for lang tid. Prøv igen.');throw error}
 finally{clearTimeout(timer)}
}
function assertEpoch(epoch:number){if(epoch!==queryCache.sessionEpoch)throw new ApiError('forbidden','Adgangen til billedet er ændret.')}
export async function uploadMedia(resourceId:string,file:File):Promise<string>{
 if(!['image/jpeg','image/png','image/gif'].includes(file.type)||!file.size||file.size>10485760)throw new ApiError('invalid','Vælg en JPG, PNG eller GIF på højst 10 MB.');
 const epoch=queryCache.sessionEpoch;
 const checksum=Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256',await file.arrayBuffer()))).map(x=>x.toString(16).padStart(2,'0')).join('');
 assertEpoch(epoch);
 const created=await api<{mediaId:string;bytesPath:string}>('createUpload',{resourceId,mime:file.type,size:file.size,checksum});
 assertEpoch(epoch);await mediaBytes(created.mediaId,file);assertEpoch(epoch);await api('finalizeUpload',{mediaId:created.mediaId});assertEpoch(epoch);return created.mediaId;
}
export async function loadMediaBlob(mediaId:string):Promise<Blob>{
 const epoch=queryCache.sessionEpoch;const blob=await mediaBytes(mediaId);
 if(epoch!==queryCache.sessionEpoch)throw new ApiError('forbidden','Adgangen til billedet er ændret.');return blob;
}
export function useMediaPreview(mediaId?:string):{url?:string;loading:boolean;error?:string}{
 const[state,setState]=useState<{id?:string;url?:string;loading:boolean;error?:string}>({loading:!!mediaId});
 useEffect(()=>{
  let alive=true,url:string|undefined;const epoch=queryCache.sessionEpoch;
  const clear=()=>{alive=false;if(url)URL.revokeObjectURL(url);setState({id:mediaId,loading:false})};
  const unsubscribe=queryCache.subscribe(event=>{if(event!=='invalidate')clear()});
  setState({id:mediaId,loading:!!mediaId});
  if(mediaId)loadMediaBlob(mediaId).then(blob=>{if(!alive||epoch!==queryCache.sessionEpoch)return;url=URL.createObjectURL(blob);setState({id:mediaId,url,loading:false})}).catch(error=>{if(alive)setState({id:mediaId,loading:false,error:error.message})});
  return()=>{alive=false;unsubscribe();if(url)URL.revokeObjectURL(url)};
 },[mediaId]);
 return state.id===mediaId?state:{loading:!!mediaId};
}
